import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { IconsComponent } from '../../shared/icons/icons.component';
import { MatRadioModule } from '@angular/material/radio';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, NavigationStart, Router, RouterEvent, RouterLink } from '@angular/router';
import * as headerConstants from '../../shared/constants/headerConst';
import { TabService } from '../../services/tabs/tab.service';
import { filter, Observable, Subject, take, takeUntil } from 'rxjs';
import { UserService } from '../../services/user/user.service';
import { SidebarService } from '../../services/sidebar-btn/sidebar.service';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { UploadService } from '../../services/upload/upload.service';
import { AuthService } from '../../services/auth/auth.service';
import { VersionService } from '../../services/version/version.service';
import { OrganizationMembership, User } from '../../interfaces/user';
import { AuthUserData } from '../../interfaces/auth';
import { environment } from '../../../environments/environment';
@Component({
  selector: 'app-header',
  standalone: true,

  imports: [
    MatButtonModule,
    FormsModule,
    MatRadioModule,
    MatToolbarModule,
    MatIconModule,
    MatMenuModule,
    MatTabsModule,
    IconsComponent,
    NgFor,
    NgIf,
    RouterLink,
    AsyncPipe,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  buttonType = headerConstants.BUTTON_TYPE;
  buttonStyle = headerConstants.BUTTON_STYLE;
  MyProfile = headerConstants.MY_PROFILE;
  chngpswd = headerConstants.CHNGPSWD;
  Logout = headerConstants.LOGOUT;
  ariaLabel = headerConstants.ARIA_LABEL;
  ariaHidden = headerConstants.ARIA_HIDDEN;
  help = headerConstants.HELP;
  account = headerConstants.ACCOUNT;
  organization = headerConstants.Organization;
  lock = headerConstants.LOCK;
  logout_icon = headerConstants.LOGOUT_ICON;
  dropdown = headerConstants.DROPDOWN;
  selectedTabIndex = 0;
  isInitialNavigation = true;

  SUPERADMIN_TABS: string[] = headerConstants.SUPERADMIN_TABS;
  ADMIN_TABS: string[] = headerConstants.ADMIN_TABS;
  USER_TABS: string[] = headerConstants.USER_TABS;
  visibleTabs: string[] = [];
  tabRoutes: string[] = [];
  private destroy$ = new Subject<void>();

  private userService = inject(UserService);
  private authService = inject(AuthService);
  private versionService = inject(VersionService);

  readonly user$ = this.userService.user$;
  readonly userOrgs$ = this.userService.userOrgs$;
  readonly authUserData$ = this.authService.authUserData$;

  // A newer build has been deployed — drives the update notice in the account menu.
  readonly updateAvailable$ = this.versionService.updateAvailable$;
  readonly versionDetectionEnabled = environment.versionDetectionEnabled;

  constructor(
    private router: Router,
    private tabService: TabService,
    private sidebarService: SidebarService,
    private messageModal: MessageModalService
  ) {}

  ngOnInit(): void {
    this.setVisibleTabs(this.authService.snapshot?.userRole ?? null);

    // Get the current route and determine the tab index
    const currentRoute = this.router.url;
    const savedTabIndex = this.tabRoutes.indexOf(currentRoute);

    // If a valid route is found, set it; otherwise, use the saved tab index
    this.selectedTabIndex = savedTabIndex !== -1 ? savedTabIndex : this.tabService.getActiveTab();

    // Sync the tab state
    this.tabService.setActiveTab(this.selectedTabIndex);
    this.handleNavigation();
  }

  selectOrganization(orgId: string) {
    this.authService
      .switchOrgContext(orgId)
      .pipe(take(1))
      .subscribe({
        error: (err) => {
          console.error(err);
          this.messageModal.openModal(
            $localize`Error`,
            $localize`Error while switching organizations, try logging in again.`
          );
          this.router.navigate(['/login']);
        },
      });
  }

  setVisibleTabs(userRole: string | null): void {
    if (userRole) {
      switch (userRole) {
        case 'SuperAdmin':
          this.visibleTabs = this.SUPERADMIN_TABS;
          this.tabRoutes = [
            '', // Placeholder for the hidden dummy tab at index 0
            '/org',
            '/applications',
            '/reports',
            // '/newsfeed', <= work in progress, disabled for now
          ];
          break;
        case 'Admin':
          this.visibleTabs = this.ADMIN_TABS;
          this.tabRoutes = [
            '/instrument-run',
            '/sv-test-panel',
            '/sample-test',
            '/user-management',
            '/file-management',
          ];
          break;
        case 'Manager':
          this.visibleTabs = this.ADMIN_TABS;
          this.tabRoutes = [
            '/instrument-run',
            '/sv-test-panel',
            '/sample-test',
            '/user-management',
            '/file-management',
          ];
          break;
        case 'User':
          this.visibleTabs = this.USER_TABS;
          this.tabRoutes = ['/instrument-run', '/sv-test-panel', '/sample-test', '/file-management'];
          break;
        default:
          this.visibleTabs = [];
          this.tabRoutes = [];
      }
    }
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  // Full page load so the new index.html and its fingerprinted bundles are fetched.
  reloadApp(): void {
    window.location.reload();
  }

  onTabChange(event: any): void {
    const tabIndex = event.index;
    const route = this.tabRoutes[tabIndex];

    this.tabService.setActiveTab(tabIndex);

    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  handleNavigation(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: NavigationEnd) => {
        const currentRoute = this.router.url;
        const tabIndex = this.tabRoutes.indexOf(currentRoute);

        if (tabIndex !== -1) {
          this.selectedTabIndex = tabIndex;
          this.tabService.setActiveTab(tabIndex);
        } else {
          this.selectedTabIndex = 0;
        }
      });
  }
  logout() {
    // Clear token and user information from local storage and redirect to login
    this.authService
      .logout()
      .pipe(take(1))
      .subscribe({
        next: () => this.router.navigate(['/login']),
        error: (err) => {
          this.messageModal.openModal($localize`Error`, $localize`Error logging out: ${err}:errorMessage:`);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
